import { BLOCK_PADDING, uid } from "../create-shared";
import {
  SOCIAL_LINK_DEFAULTS,
} from "../social-link-defaults";
import type { SocialBlock, SocialBlockLink, SocialBlockLinkNetwork } from "../types";
import type { BlockDefinition } from "./types";

export const DEFAULT_SOCIAL_BLOCK_FACEBOOK_LINK_ID =
  'a1111111-1111-4111-8111-111111111111';

export const DEFAULT_SOCIAL_BLOCK_INSTAGRAM_LINK_ID =
  'a1222222-2222-4222-8222-222222222222';

export const DEFAULT_SOCIAL_BLOCK_TWITTER_LINK_ID =
  'a1333333-3333-4333-8333-333333333333';

export const TEMPLATE_SOCIAL_FACEBOOK_LINK_ID =
  'a2111111-1111-4111-8111-111111111111';

export const TEMPLATE_SOCIAL_INSTAGRAM_LINK_ID =
  'a2222222-2222-4222-8222-222222222222';

export const TEMPLATE_SOCIAL_TWITTER_LINK_ID =
  'a3333333-3333-4333-8333-333333333333';

export const TEMPLATE_SOCIAL_BLOCK_ID =
  's1111111-1111-4111-8111-111111111111';

export function socialBlockLink(
  network: SocialBlockLinkNetwork,
  url = '',
  options: {
    id?: string;
    icon?: SocialBlockLink['icon'];
    title?: string;
    alt?: string;
  } = {},
): SocialBlockLink {
  const link: SocialBlockLink = {
    id: options.id ?? uid(),
    network,
    url,
    icon: options.icon ?? null,
  };

  if (options.title !== undefined) {
    link.title = options.title;
  }

  if (options.alt !== undefined) {
    link.alt = options.alt;
  }

  return link;
}

function presetLink(
  id: string,
  network: keyof typeof SOCIAL_LINK_DEFAULTS,
): SocialBlockLink {
  const defaults = SOCIAL_LINK_DEFAULTS[network];
  return socialBlockLink(network, defaults.url, {
    id,
    title: defaults.title,
    alt: defaults.alt,
  });
}

export function defaultSocialBlockLinks(): SocialBlockLink[] {
  return [
    presetLink(DEFAULT_SOCIAL_BLOCK_FACEBOOK_LINK_ID, 'facebook'),
    presetLink(DEFAULT_SOCIAL_BLOCK_INSTAGRAM_LINK_ID, 'instagram'),
    presetLink(DEFAULT_SOCIAL_BLOCK_TWITTER_LINK_ID, 'twitter'),
  ];
}

export function templateSocialBlockLinks(): SocialBlockLink[] {
  return [
    presetLink(TEMPLATE_SOCIAL_FACEBOOK_LINK_ID, 'facebook'),
    presetLink(TEMPLATE_SOCIAL_INSTAGRAM_LINK_ID, 'instagram'),
    presetLink(TEMPLATE_SOCIAL_TWITTER_LINK_ID, 'twitter'),
  ];
}

export function createSocialBlock(): SocialBlock {
  return {
    id: uid(),
    type: 'social',
    links: defaultSocialBlockLinks(),
    iconVariant: 'white',
    align: 'center',
    iconSize: 16,
    gap: 12,
    padding: { ...BLOCK_PADDING },
  };
}

export function createTemplateSocialBlock(
  id: string = TEMPLATE_SOCIAL_BLOCK_ID,
): SocialBlock {
  return {
    id,
    type: 'social',
    links: templateSocialBlockLinks(),
    iconVariant: 'white',
    align: 'center',
    iconSize: 16,
    gap: 12,
    padding: { ...BLOCK_PADDING },
  };
}

export const socialBlockDefinition: BlockDefinition = {
  type: 'social',
  label: 'Social links',
  create: createSocialBlock,
};
